import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { firestore } from "config/firebase.js";

import {
    Grid,
    Row,
    Col,
    Button,
    Modal,
    Form,
    FormGroup,
    ControlLabel,
    FormControl
} from "react-bootstrap";
import Card from "components/Card/Card.jsx";

class UserManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            users: [],
            loading: true,
            error: null,
            modal: false,
            detailsUser: null,
            selectedUser: null,
            editMode: false,
            formData: {
                name: "",
                email: "",
                role: "user",
                status: "active",
                subscription: "basic"
            },
            currentPage: 1,
            usersPerPage: 10,
            filteredUsers: [],
            searchTerm: '',
            filters: {
                subscription: 'all',
                role: 'all',
                status: 'all'
            }
        };
    }

    componentDidMount() {
        this.loadUsers();
    }

    loadUsers = async () => {
        try {
            this.setState({ loading: true, error: null });

            const usersSnapshot = await firestore.collection('users').get();
            const users = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();

                let subscriptionAmount = 0;
                const planType = userData.subscription?.plan || 'basic';

                switch (planType) {
                    case 'premium':
                        subscriptionAmount = 5000;
                        break;
                    case 'annual':
                        subscriptionAmount = 50000;
                        break;
                    case 'quarterly':
                        subscriptionAmount = 15000;
                        break;
                    default:
                        subscriptionAmount = 0;
                }

                const user = {
                    id: doc.id,
                    name: userData.name || userData.firstName || userData.displayName || 'Nom non défini',
                    email: userData.email || 'N/A',
                    role: userData.role || 'user',
                    status: userData.status || 'active',
                    subscription: userData.subscription?.plan || userData.subscription?.status || 'basic',
                    subscriptionStatus: userData.subscription?.status || 'inactive',
                    profession: userData.profession || 'N/A',
                    telephone: userData.telephone || 'N/A',
                    numeroOrdre: userData.numeroOrdre || userData.numero || userData.registrationNumber || userData.numero_enregistrement || 'N/A',
                    lastLogin: userData.lastActive ? this.formatDate(userData.lastActive) : 'Jamais',
                    createdAt: userData.date || userData.createdAt ? this.formatDate(userData.date || userData.createdAt) : 'Inconnue',
                    emailVerified: userData.emailVerified || false,
                    subscriptionAmount: subscriptionAmount,
                    subscriptionStartDate: userData.subscription?.startDate ? this.formatDate(userData.subscription.startDate) : 'N/A'
                };

                users.push(user);
            });

            users.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            this.setState({
                users,
                filteredUsers: users,
                loading: false
            });

        } catch (error) {
            this.setState({
                error: error.message,
                loading: false
            });
        }
    }

    formatDate(dateValue) {
        if (!dateValue) return 'Inconnue';

        try {
            let date;

            if (dateValue.toDate) {
                date = dateValue.toDate();
            }
            else if (typeof dateValue === 'string' && dateValue.includes(' ')) {
                const parts = dateValue.split(' ');
                if (parts.length === 3) {
                    date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                } else {
                    date = new Date(dateValue);
                }
            }
            else {
                date = new Date(dateValue);
            }

            return date.toLocaleDateString('fr-FR');
        } catch (error) {
            return 'Format invalide';
        }
    }

    handleSearch = (searchTerm) => {
        const { users } = this.state;

        this.setState({
            searchTerm,
            filteredUsers: this.applyFilters(users, this.state.filters, searchTerm),
            currentPage: 1
        });
    }

    handleFilterChange = (filterName, value) => {
        this.setState(prevState => {
            const newFilters = {
                ...prevState.filters,
                [filterName]: value
            };

            return {
                filters: newFilters,
                filteredUsers: this.applyFilters(prevState.users, newFilters, prevState.searchTerm),
                currentPage: 1
            };
        });
    }

    applyFilters = (users, filters, searchTerm = '') => {
        let result = [...users];

        if (searchTerm && searchTerm.trim() !== '') {
            const search = searchTerm.toLowerCase().trim();
            result = result.filter(user =>
                (user.name && user.name.toLowerCase().includes(search)) ||
                (user.email && user.email.toLowerCase().includes(search)) ||
                (user.profession && user.profession.toLowerCase().includes(search)) ||
                (user.numeroOrdre && String(user.numeroOrdre).toLowerCase().includes(search)) ||
                (user.telephone && user.telephone.toLowerCase().includes(search)) ||
                (user.role && user.role.toLowerCase().includes(search)) ||
                (user.subscription && user.subscription.toLowerCase().includes(search))
            );
        }

        if (filters.subscription !== 'all') {
            result = result.filter(user => user.subscription === filters.subscription);
        }

        if (filters.role !== 'all') {
            result = result.filter(user => user.role === filters.role);
        }

        if (filters.status !== 'all') {
            result = result.filter(user => user.status === filters.status);
        }

        return result;
    }

    handlePageChange = (pageNumber) => {
        this.setState({ currentPage: pageNumber });
    }

    getPaginatedUsers = () => {
        const { filteredUsers, currentPage, usersPerPage } = this.state;
        const startIndex = (currentPage - 1) * usersPerPage;
        const endIndex = startIndex + usersPerPage;
        return filteredUsers.slice(startIndex, endIndex);
    }

    getTotalPages = () => {
        const { filteredUsers, usersPerPage } = this.state;
        return Math.ceil(filteredUsers.length / usersPerPage);
    }

    toggleModal = () => {
        this.setState(prevState => ({
            modal: !prevState.modal,
            editMode: false,
            selectedUser: null,
            formData: {
                name: "",
                email: "",
                role: "user",
                status: "active",
                subscription: "basic"
            }
        }));
    }

    editUser = (user) => {
        this.setState({
            modal: true,
            editMode: true,
            selectedUser: user,
            formData: {
                name: user.name,
                email: user.email,
                role: user.role,
                status: user.status,
                subscription: user.subscription
            }
        });
    }

    showUserDetails = (user) => {
        this.setState({ detailsUser: user });
    }

    closeUserDetails = () => {
        this.setState({ detailsUser: null });
    }

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: value
            }
        }));
    }

    handleSubmit = async () => {
        const { formData, editMode, selectedUser } = this.state;

        try {
            this.setState({ loading: true });

            if (editMode) {
                // Modifier un utilisateur existant
                await firestore.collection('users').doc(selectedUser.id).update({
                    name: formData.name,
                    email: formData.email,
                    role: formData.role,
                    status: formData.status,
                    subscription: {
                        plan: formData.subscription,
                        status: formData.status === 'active' ? 'active' : 'inactive'
                    },
                    updatedAt: new Date()
                });
            } else {
                // Ajouter un nouvel utilisateur
                await firestore.collection('users').add({
                    name: formData.name,
                    email: formData.email,
                    role: formData.role,
                    status: formData.status,
                    subscription: {
                        plan: formData.subscription,
                        status: formData.status === 'active' ? 'active' : 'inactive'
                    },
                    createdAt: new Date(),
                    emailVerified: false,
                    lastActive: null
                });
            }

            // Recharger la liste des utilisateurs
            await this.loadUsers();

            this.toggleModal();

            if (this.props.showNotification) {
                this.props.showNotification(
                    `Utilisateur ${editMode ? 'modifié' : 'ajouté'} avec succès`,
                    'success'
                );
            }

        } catch (error) {
            this.setState({
                error: `Erreur lors de la sauvegarde: ${error.message}`,
                loading: false
            });

            if (this.props.showNotification) {
                this.props.showNotification(
                    `Erreur lors de la ${editMode ? 'modification' : 'création'} de l'utilisateur`,
                    'error'
                );
            }
        }
    }

    deleteUser = async (userId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            try {
                this.setState({ loading: true });

                // Supprimer de Firebase
                await firestore.collection('users').doc(userId).delete();

                // Recharger la liste
                await this.loadUsers();

                if (this.props.showNotification) {
                    this.props.showNotification('Utilisateur supprimé avec succès', 'success');
                }
            } catch (error) {
                this.setState({
                    error: `Erreur lors de la suppression: ${error.message}`,
                    loading: false
                });

                if (this.props.showNotification) {
                    this.props.showNotification('Erreur lors de la suppression de l\'utilisateur', 'error');
                }
            }
        }
    }

    getStatusBadge = (status) => {
        const colors = {
            active: 'success',
            inactive: 'default',
            suspended: 'warning',
            banned: 'danger'
        };
        return <span className={`label label-${colors[status]}`}>{status}</span>;
    }

    getRoleBadge = (role) => {
        const colors = {
            admin: 'danger',
            user: 'info',
            moderator: 'warning'
        };
        return <span className={`label label-${colors[role]}`}>{role}</span>;
    }

    render() {
        const { modal, formData, editMode, loading, error, currentPage, filteredUsers, searchTerm } = this.state;

        return (
            <div className="main-content user-management-page">
                <Grid fluid>
                    <Row>
                        <Col lg={12} md={12}>
                            <Card
                                content={
                                    <div>
                                        {/* Action Buttons */}
                                        <div className="action-buttons">
                                            <Button
                                                bsStyle="primary"
                                                bsSize="sm"
                                                onClick={this.toggleModal}
                                                className="btn-add-user"
                                            >
                                                <i className="fa fa-plus" /> Ajouter un utilisateur
                                            </Button>
                                            <Button
                                                bsStyle="success"
                                                bsSize="sm"
                                                onClick={this.loadUsers}
                                                disabled={loading}
                                                className="btn-refresh"
                                            >
                                                <i className={`fa fa-${loading ? 'spinner fa-spin' : 'refresh'}`} /> {loading ? 'Actualisation...' : 'Actualiser'}
                                            </Button>
                                        </div>

                                        {/* Loading State */}
                                        {loading && (
                                            <div className="loading-state">
                                                <i className="fa fa-spinner fa-spin fa-2x"></i>
                                                <p>Chargement des utilisateurs...</p>
                                            </div>
                                        )}

                                        {/* Error State */}
                                        {error && (
                                            <div className="alert alert-danger error-state">
                                                <strong>Erreur:</strong> {error}
                                            </div>
                                        )}

                                        {/* Main Content */}
                                        {!loading && !error && (
                                            <div className="user-management-content">
                                                {/* Search and Stats Bar */}
                                                <div className="search-stats-bar">
                                                    <div className="total-users">
                                                        <i className="fa fa-users"></i>
                                                        <span>Total: {filteredUsers.length} / {this.state.users.length} utilisateur(s)</span>
                                                    </div>

                                                    <div className="search-container">
                                                        <FormControl
                                                            type="text"
                                                            placeholder="Rechercher un utilisateur..."
                                                            value={searchTerm}
                                                            onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                                            onKeyPress={(e) => e.key === 'Enter' && this.handleSearch(this.state.searchTerm)}
                                                            className="search-input"
                                                        />
                                                        <Button
                                                            type="button"
                                                            bsStyle="primary"
                                                            bsSize="sm"
                                                            onClick={() => this.handleSearch(this.state.searchTerm)}
                                                            className="search-button"
                                                        >
                                                            <i className="fa fa-search" />
                                                        </Button>
                                                    </div>

                                                    <div className="user-stats">
                            <span className="stat verified">
                              <i className="fa fa-check-circle"></i> Actifs: {this.state.users.filter(u => u.emailVerified).length}
                            </span>
                                                        <span className="stat premium">
                              <i className="fa fa-star"></i> Premium: {this.state.users.filter(u => u.subscription === 'premium').length}
                            </span>
                                                    </div>
                                                </div>

                                                {/* Filters Section */}
                                                <div className="filters-section">
                                                    <div className="filters-header">
                                                        <i className="fa fa-filter"></i>
                                                        <span>Filtres:</span>
                                                    </div>

                                                    <div className="filter-group">
                                                        <span>Abonnement:</span>
                                                        <FormControl
                                                            componentClass="select"
                                                            value={this.state.filters.subscription}
                                                            onChange={(e) => this.handleFilterChange('subscription', e.target.value)}
                                                        >
                                                            <option value="all">Tous</option>
                                                            <option value="annual">Annuel</option>
                                                            <option value="quarterly">Trimestriel</option>
                                                            <option value="premium">Mensuel</option>
                                                            <option value="basic">Basique</option>
                                                        </FormControl>
                                                    </div>

                                                    <div className="filter-group">
                                                        <span>Rôle:</span>
                                                        <FormControl
                                                            componentClass="select"
                                                            value={this.state.filters.role}
                                                            onChange={(e) => this.handleFilterChange('role', e.target.value)}
                                                        >
                                                            <option value="all">Tous</option>
                                                            <option value="admin">Admin</option>
                                                            <option value="user">Utilisateur</option>
                                                            <option value="moderator">Modérateur</option>
                                                        </FormControl>
                                                    </div>

                                                    <div className="filter-group">
                                                        <span>Statut:</span>
                                                        <FormControl
                                                            componentClass="select"
                                                            value={this.state.filters.status}
                                                            onChange={(e) => this.handleFilterChange('status', e.target.value)}
                                                        >
                                                            <option value="all">Tous</option>
                                                            <option value="active">Actif</option>
                                                            <option value="inactive">Inactif</option>
                                                            <option value="suspended">Suspendu</option>
                                                        </FormControl>
                                                    </div>

                                                    <Button
                                                        bsSize="sm"
                                                        onClick={() => this.setState({
                                                            filters: { subscription: 'all', role: 'all', status: 'all' },
                                                            filteredUsers: this.state.users,
                                                            searchTerm: '',
                                                            currentPage: 1
                                                        })}
                                                        className="reset-filters"
                                                    >
                                                        <i className="fa fa-times" /> Réinitialiser
                                                    </Button>
                                                </div>

                                                {/* Users Table */}
                                                <div className="users-table-container">
                                                    <table className="users-table">
                                                        <thead>
                                                        <tr>
                                                            <th><i className="fa fa-user"></i> Nom</th>
                                                            <th><i className="fa fa-envelope"></i> Email</th>
                                                            <th><i className="fa fa-user-md"></i> Profession</th>
                                                            <th><i className="fa fa-user-tag"></i> Rôle</th>
                                                            <th><i className="fa fa-circle"></i> Statut</th>
                                                            <th><i className="fa fa-calendar"></i> Inscription</th>
                                                            <th><i className="fa fa-cogs"></i> Actions</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {this.getPaginatedUsers().map(user => (
                                                            <tr key={user.id}>
                                                                <td>
                                                                    <div className="user-name">
                                                                        <strong style={{color: '#2c3e50', fontSize: '15px'}}>{user.name}</strong>
                                                                        {user.telephone && (
                                                                            <div className="user-phone">
                                                                                <i className="fa fa-phone" style={{marginRight: '5px'}}></i>
                                                                                {user.telephone}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="user-email">
                                                                        <strong>{user.email}</strong>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="label label-profession">{user.profession}</span>
                                                                </td>
                                                                <td>{this.getRoleBadge(user.role)}</td>
                                                                <td>
                                                                    <span className={`user-status-badge ${user.status === 'active' ? 'is-active' : 'is-inactive'}`}>
                                                                        {user.status === 'active' ? 'Actif' : user.status}
                                                                    </span>
                                                                </td>
                                                                <td><span className="created-at">{user.createdAt}</span></td>
                                                                <td>
                                                                    <div className="action-buttons">
                                                                        <Button
                                                                            bsStyle="default"
                                                                            bsSize="xs"
                                                                            onClick={() => this.showUserDetails(user)}
                                                                            title="Détails"
                                                                            className="btn-user-details"
                                                                        >
                                                                            <i className="fa fa-eye" /> Détails
                                                                        </Button>
                                                                        <Button
                                                                            bsStyle="info"
                                                                            bsSize="xs"
                                                                            onClick={() => this.editUser(user)}
                                                                            title="Modifier"
                                                                            className="btn-edit"
                                                                        >
                                                                            <i className="fa fa-edit" />
                                                                        </Button>
                                                                        <Button
                                                                            bsStyle="danger"
                                                                            bsSize="xs"
                                                                            onClick={() => this.deleteUser(user.id)}
                                                                            title="Supprimer"
                                                                            className="btn-delete"
                                                                        >
                                                                            <i className="fa fa-trash" />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Pagination */}
                                                {this.getTotalPages() > 1 && (
                                                    <div className="pagination-container">
                                                        <Button
                                                            bsStyle="default"
                                                            bsSize="sm"
                                                            disabled={currentPage === 1}
                                                            onClick={() => this.handlePageChange(currentPage - 1)}
                                                            className="pagination-btn"
                                                        >
                                                            <i className="fa fa-chevron-left" />
                                                        </Button>

                                                        {Array.from({ length: this.getTotalPages() }, (_, i) => i + 1).map(pageNum => (
                                                            <Button
                                                                key={pageNum}
                                                                bsStyle={pageNum === currentPage ? "primary" : "default"}
                                                                bsSize="sm"
                                                                onClick={() => this.handlePageChange(pageNum)}
                                                                className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                                                            >
                                                                {pageNum}
                                                            </Button>
                                                        ))}

                                                        <Button
                                                            bsStyle="default"
                                                            bsSize="sm"
                                                            disabled={currentPage === this.getTotalPages()}
                                                            onClick={() => this.handlePageChange(currentPage + 1)}
                                                            className="pagination-btn"
                                                        >
                                                            <i className="fa fa-chevron-right" />
                                                        </Button>

                                                        <span className="pagination-info">
                              Page {currentPage} sur {this.getTotalPages()}
                                                            ({(currentPage - 1) * this.state.usersPerPage + 1}-{Math.min(currentPage * this.state.usersPerPage, filteredUsers.length)} sur {filteredUsers.length})
                            </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>

                <Modal show={!!this.state.detailsUser} onHide={this.closeUserDetails} className="user-details-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Détails de l'utilisateur</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.detailsUser && (
                            <div className="user-details-grid">
                                <div><span>Nom</span><strong>{this.state.detailsUser.name}</strong></div>
                                <div><span>Email</span><strong>{this.state.detailsUser.email}</strong></div>
                                <div><span>Profession</span><strong>{this.state.detailsUser.profession}</strong></div>
                                <div><span>Téléphone</span><strong>{this.state.detailsUser.telephone}</strong></div>
                                <div><span>N° Ordre</span><strong>{this.state.detailsUser.numeroOrdre}</strong></div>
                                <div><span>Rôle</span><strong>{this.state.detailsUser.role}</strong></div>
                                <div><span>Abonnement</span><strong>{this.state.detailsUser.subscription} · {this.state.detailsUser.subscriptionStatus}</strong></div>
                                <div><span>Montant</span><strong>{this.state.detailsUser.subscriptionAmount > 0 ? `${this.state.detailsUser.subscriptionAmount.toLocaleString()} F` : '—'}</strong></div>
                                <div><span>Email vérifié</span><strong>{this.state.detailsUser.emailVerified ? 'Oui' : 'Non'}</strong></div>
                                <div><span>Dernière connexion</span><strong>{this.state.detailsUser.lastLogin}</strong></div>
                                <div><span>Date d'inscription</span><strong>{this.state.detailsUser.createdAt}</strong></div>
                                {this.state.detailsUser.subscriptionAmount > 0 && (
                                    <div><span>Début abonnement</span><strong>{this.state.detailsUser.subscriptionStartDate}</strong></div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.closeUserDetails}>Fermer</Button>
                    </Modal.Footer>
                </Modal>

                {/* User Modal */}
                <Modal show={modal} onHide={this.toggleModal} bsSize="lg" className="user-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className={`fa ${editMode ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                            {editMode ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="user-form">
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Nom complet</ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={this.handleInputChange}
                                            required
                                            placeholder="Nom complet de l'utilisateur"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Email</ControlLabel>
                                        <FormControl
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={this.handleInputChange}
                                            required
                                            placeholder="email@exemple.com"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Rôle</ControlLabel>
                                        <FormControl
                                            componentClass="select"
                                            name="role"
                                            value={formData.role}
                                            onChange={this.handleInputChange}
                                        >
                                            <option value="user">Utilisateur</option>
                                            <option value="admin">Administrateur</option>
                                            <option value="moderator">Modérateur</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Statut</ControlLabel>
                                        <FormControl
                                            componentClass="select"
                                            name="status"
                                            value={formData.status}
                                            onChange={this.handleInputChange}
                                        >
                                            <option value="active">Actif</option>
                                            <option value="inactive">Inactif</option>
                                            <option value="suspended">Suspendu</option>
                                            <option value="banned">Banni</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Abonnement</ControlLabel>
                                        <FormControl
                                            componentClass="select"
                                            name="subscription"
                                            value={formData.subscription}
                                            onChange={this.handleInputChange}
                                        >
                                            <option value="basic">Basique</option>
                                            <option value="premium">Premium</option>
                                            <option value="annual">Annuel</option>
                                            <option value="quarterly">Trimestriel</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            bsStyle="default"
                            onClick={this.toggleModal}
                            className="btn-cancel"
                            disabled={loading}
                        >
                            Annuler
                        </Button>
                        <Button
                            bsStyle="primary"
                            onClick={this.handleSubmit}
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" /> Sauvegarde...
                                </>
                            ) : (
                                editMode ? 'Modifier' : 'Ajouter'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* CSS Styles */}
                <style jsx>{`
                    .user-management-header {
                        background: var(--app-primary-color);
                        border-radius: 15px;
                        box-shadow: 0 4px 15px rgba(var(--app-primary-rgb), 0.3);
                        padding: 40px 20px;
                        margin-bottom: 30px;
                        text-align: center;
                        color: white;
                    }

                    .header-content h2 {
                        color: white;
                        margin-bottom: 15px;
                        font-size: 32px;
                        font-weight: 600;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    }

                    .header-content p {
                        color: white;
                        font-size: 18px;
                        margin: 0;
                        font-weight: 300;
                    }

                    .header-icon {
                        font-size: 48px;
                        color: #ffffff;
                        margin-bottom: 15px;
                        display: block;
                        opacity: 0.9;
                    }

                    .action-buttons {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                    }

                    .btn-add-user {
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        color: white;
                        box-shadow: 0 4px 15px rgba(var(--app-primary-rgb), 0.3);
                        transition: all 0.3s ease;
                    }

                    .btn-add-user:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(var(--app-primary-rgb), 0.4);
                    }

                    .btn-refresh {
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        color: white;
                        box-shadow: 0 4px 15px rgba(var(--app-primary-rgb), 0.3);
                        transition: all 0.3s ease;
                    }

                    .btn-refresh:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(var(--app-primary-rgb), 0.4);
                    }

                    .loading-state {
                        text-align: center;
                        padding: 40px;
                        color: var(--app-primary-color);
                    }

                    .error-state {
                        margin-top: 20px;
                        border-radius: 8px;
                        background-color: #ff5555;
                        color: white;
                        border: none;
                    }

                    .search-stats-bar {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        background-color: #f8f9fa;
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        flex-wrap: wrap;
                        gap: 15px;
                    }

                    .total-users {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-weight: 600;
                        color: var(--app-primary-color);
                    }

                    .search-input {
                        flex-grow: 1;
                        max-width: 400px;
                        border-radius: 20px;
                        padding: 8px 15px;
                        border: 2px solid var(--app-primary-color);
                        transition: all 0.3s ease;
                    }

                    .search-input:focus {
                        border-color: var(--app-primary-color);
                        box-shadow: 0 0 0 0.2rem rgba(var(--app-primary-rgb), 0.25);
                    }

                    .user-stats {
                        display: flex;
                        gap: 10px;
                    }

                    .stat {
                        padding: 5px 12px;
                        border-radius: 15px;
                        font-size: 12px;
                        color: white;
                    }

                    .verified {
                        background-color: var(--app-primary-color);
                    }

                    .premium {
                        background-color: #f39c12;
                    }

                    .filters-section {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        background: var(--app-primary-color);
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        color: #ffffff;
                        box-shadow: 0 4px 15px rgba(var(--app-primary-rgb), 0.3);
                        flex-wrap: wrap;
                        gap: 15px;
                    }

                    .filters-header {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        color: #ffffff;
                        font-weight: 600;
                    }

                    .filter-group {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .filter-group span {
                        color: #ffffff;
                        opacity: 0.9;
                    }

                    .reset-filters {
                        background: rgba(255, 255, 255, 0.2);
                        color: #ffffff;
                        border: 1px solid rgba(255, 255, 255, 0.3);
                        font-weight: 600;
                        transition: all 0.3s ease;
                    }

                    .reset-filters:hover {
                        background: rgba(255, 255, 255, 0.3);
                        transform: translateY(-1px);
                    }

                    .users-table-container {
                        background-color: white;
                        border-radius: 10px;
                        overflow: hidden;
                        box-shadow: 0 4px 15px rgba(var(--app-primary-rgb), 0.1);
                        margin-top: 20px;
                        overflow-x: auto;
                        border: 1px solid rgba(var(--app-primary-rgb), 0.1);
                    }

                    .users-table {
                        width: 100%;
                        margin: 0;
                    }

                    .users-table th {
                        background: var(--app-primary-color);
                        padding: 15px 10px;
                        border-top: none;
                        color: #ffffff;
                        font-weight: 600;
                        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                    }

                    .users-table td {
                        padding: 12px 10px;
                        vertical-align: middle;
                        border-top: 1px solid #f1f1f1;
                    }

                    .users-table tr:hover {
                        background-color: rgba(var(--app-primary-rgb), 0.05);
                        transition: background-color 0.3s ease;
                    }

                    .user-email strong {
                        color: var(--app-primary-color);
                    }

                    .user-phone {
                        font-size: 12px;
                        color: #6c757d;
                        margin-top: 2px;
                    }

                    .label-profession {
                        background-color: var(--app-primary-color);
                    }

                    .user-numero {
                        font-family: monospace;
                        font-size: 13px;
                    }

                    .subscription-badge {
                        display: inline-block;
                        padding: 5px 10px;
                        border-radius: 4px;
                        color: white;
                        font-weight: 600;
                        font-size: 12px;
                    }

                    .subscription-badge.premium {
                        background-color: #f39c12;
                    }

                    .subscription-badge.annual {
                        background-color: #e74c3c;
                    }

                    .subscription-badge.quarterly {
                        background-color: #3498db;
                    }

                    .subscription-badge.basic {
                        background-color: #95a5a6;
                    }

                    .subscription-status {
                        font-size: 11px;
                        color: #6c757d;
                        margin-top: 2px;
                    }

                    .amount-badge {
                        display: inline-block;
                        padding: 5px 10px;
                        border-radius: 4px;
                        font-size: 12px;
                    }

                    .amount-badge.active {
                        background-color: #2ecc71;
                        color: white;
                    }

                    .amount-badge:not(.active) {
                        background-color: #ecf0f1;
                        color: #7f8c8d;
                    }

                    .subscription-date {
                        font-size: 11px;
                        color: #6c757d;
                        margin-top: 2px;
                    }

                    .verification-badge {
                        display: inline-block;
                        padding: 5px 10px;
                        border-radius: 4px;
                        font-weight: bold;
                    }

                    .verification-badge.verified {
                        background-color: #2ecc71;
                        color: white;
                    }

                    .verification-badge.not-verified {
                        background-color: #f39c12;
                        color: white;
                    }

                    .last-login, .created-at {
                        font-size: 13px;
                    }

                    .action-buttons {
                        display: flex;
                        gap: 5px;
                        justify-content: center;
                    }

                    .btn-edit {
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        transition: all 0.3s ease;
                    }

                    .btn-edit:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 4px 8px rgba(var(--app-primary-rgb), 0.3);
                    }

                    .btn-delete {
                        background-color: #e74c3c;
                        border-color: #e74c3c;
                        transition: all 0.3s ease;
                    }

                    .btn-delete:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 4px 8px rgba(231, 76, 60, 0.3);
                    }

                    .pagination-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        margin-top: 20px;
                        gap: 10px;
                        flex-wrap: wrap;
                    }

                    .pagination-btn {
                        min-width: 40px;
                        border-radius: 20px;
                        border-color: var(--app-primary-color);
                        transition: all 0.3s ease;
                    }

                    .pagination-btn.active {
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        color: white;
                    }

                    .pagination-btn:hover {
                        border-color: var(--app-primary-color);
                        color: var(--app-primary-color);
                    }

                    .pagination-info {
                        margin-left: 15px;
                        color: var(--app-primary-color);
                        font-size: 14px;
                        font-weight: 500;
                    }

                    .user-modal :global(.modal-header) {
                        background: var(--app-primary-color);
                        color: white;
                        border-bottom: none;
                        border-radius: 10px 10px 0 0;
                    }

                    .user-modal :global(.modal-title) {
                        color: #ffffff;
                    }

                    .user-modal :global(.close) {
                        color: white;
                        opacity: 0.8;
                    }

                    .user-modal :global(.modal-body) {
                        padding: 25px;
                    }

                    .user-modal :global(.modal-footer) {
                        border-top: none;
                        padding: 15px 25px;
                        background-color: #f8f9fa;
                        border-radius: 0 0 10px 10px;
                    }

                    .btn-cancel {
                        background-color: #95a5a6;
                        border-color: #95a5a6;
                        color: white;
                    }

                    .btn-submit {
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        color: white;
                        font-weight: 600;
                        transition: all 0.3s ease;
                    }

                    .btn-submit:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 20px rgba(var(--app-primary-rgb), 0.4);
                    }

                    .user-management-page {
                        min-height: 100vh;
                        padding: 24px;
                        background: #f6f9fa;
                    }

                    .user-management-page .user-management-header {
                        padding: 24px 28px;
                        margin-bottom: 20px;
                        color: #17313d;
                        text-align: left;
                        background: linear-gradient(120deg, #fff 0%, #f2fbfd 100%);
                        border: 1px solid #e4eef1;
                        border-radius: 15px;
                        box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                    }

                    .user-management-page .header-icon {
                        display: inline-block;
                        margin: 0 12px 0 0;
                        color: #087f95;
                        font-size: 25px;
                        vertical-align: middle;
                    }

                    .user-management-page .header-content h2 {
                        display: inline-block;
                        margin: 0;
                        color: #17313d;
                        font-size: 25px;
                        font-weight: 700;
                        text-shadow: none;
                        vertical-align: middle;
                    }

                    .user-management-page .header-content p {
                        margin: 8px 0 0;
                        color: #64748b;
                        font-size: 13px;
                    }

                    .user-management-page .action-buttons {
                        justify-content: flex-start;
                        gap: 10px;
                        flex-wrap: wrap;
                    }

                    .user-management-page .btn-add-user,
                    .user-management-page .btn-refresh,
                    .user-management-page .btn-add-user:hover,
                    .user-management-page .btn-refresh:hover {
                        min-height: 40px;
                        padding: 9px 14px;
                        color: #073b46;
                        background: var(--app-primary-color);
                        border: 1px solid var(--app-primary-color);
                        border-radius: 8px;
                        box-shadow: 0 4px 12px rgba(var(--app-primary-rgb), 0.16);
                        font-weight: 600;
                    }

                    .user-management-page .btn-refresh {
                        color: #475569;
                        background: #fff;
                        border-color: #d7e1e5;
                        box-shadow: none;
                    }

                    .user-management-page .search-stats-bar {
                        padding: 16px;
                        background: #fff;
                        border: 1px solid #e3ebee;
                        border-radius: 12px;
                    }

                    .user-management-page .search-container {
                        display: flex;
                        flex: 0 1 400px;
                        align-items: stretch;
                        gap: 8px;
                        min-width: 260px;
                    }

                    .user-management-page .search-input {
                        flex: 1 1 auto;
                        width: 100%;
                        max-width: none;
                        min-width: 0;
                        height: 38px;
                        border: 1px solid #d7e1e5;
                        border-radius: 8px !important;
                    }

                    .user-management-page .search-input:focus {
                        border-color: var(--app-primary-color);
                        box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.14);
                    }

                    .user-management-page .search-button,
                    .user-management-page .search-button:focus,
                    .user-management-page .search-button:active,
                    .user-management-page .search-button:hover {
                        display: inline-flex;
                        flex: 0 0 42px;
                        align-items: center;
                        justify-content: center;
                        width: 42px;
                        min-height: 34px;
                        height: 38px;
                        padding: 0;
                        color: #073b46;
                        background: var(--app-primary-color);
                        border-color: var(--app-primary-color);
                        border-radius: 8px;
                    }

                    .user-management-page .search-button .fa {
                        display: inline-block;
                        visibility: visible;
                        font-family: FontAwesome !important;
                        font-size: 14px;
                        line-height: 1;
                    }

                    .user-management-page .users-table .action-buttons {
                        display: flex;
                        align-items: center;
                        justify-content: flex-start;
                        gap: 5px;
                        min-width: 142px;
                        white-space: nowrap;
                    }

                    .user-management-page .users-table .btn-delete,
                    .user-management-page .users-table .btn-edit {
                        display: inline-flex;
                        flex: 0 0 30px;
                        align-items: center;
                        justify-content: center;
                        width: 30px;
                        height: 28px;
                        min-width: 30px;
                        padding: 0;
                        line-height: 1;
                    }

                    .user-management-page .users-table .btn-delete .fa,
                    .user-management-page .users-table .btn-edit .fa {
                        display: inline-block;
                        visibility: visible;
                        font-family: FontAwesome !important;
                        font-size: 13px;
                        line-height: 1;
                    }

                    .user-management-page .filters-section {
                        justify-content: flex-start;
                        padding: 14px 16px;
                        color: #475569;
                        background: #fff;
                        border: 1px solid #e3ebee;
                        border-radius: 12px;
                        box-shadow: none;
                    }

                    .user-management-page .filters-header,
                    .user-management-page .filter-group span {
                        color: #526574;
                    }

                    .user-management-page .filter-group .form-control {
                        min-width: 130px;
                        height: 36px;
                        color: #334155;
                        background: #fff;
                        border: 1px solid #d7e1e5;
                        border-radius: 7px;
                        box-shadow: none;
                    }

                    .user-management-page .reset-filters {
                        padding: 8px 12px;
                        color: #526574;
                        background: #f7fafb;
                        border: 1px solid #d7e1e5;
                        border-radius: 7px;
                    }

                    .user-management-page .users-table-container {
                        border: 0;
                        border-radius: 12px;
                        box-shadow: 0 7px 22px rgba(31, 58, 69, 0.05);
                    }

                    .user-management-page .btn-user-details {
                        color: #087f95;
                        background: #eefafd;
                        border: 1px solid rgba(var(--app-primary-rgb), 0.22);
                        border-radius: 6px;
                        white-space: nowrap;
                    }

                    .user-management-page .user-status-badge {
                        display: inline-block;
                        padding: 4px 9px;
                        color: #475569;
                        background: #f1f5f9;
                        border-radius: 999px;
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: capitalize;
                    }

                    .user-management-page .user-status-badge.is-active {
                        color: #18794e;
                        background: #e9f8ef;
                    }

                    .user-details-modal :global(.modal-content) {
                        overflow: hidden;
                        border: 1px solid #e2ecef;
                        border-radius: 14px;
                        box-shadow: 0 20px 60px rgba(31, 58, 69, 0.16);
                    }

                    .user-details-modal :global(.modal-header) {
                        color: #17313d;
                        background: linear-gradient(120deg, #fff 0%, #f2fbfd 100%);
                        border-bottom: 1px solid #e4eef1;
                    }

                    .user-details-modal :global(.modal-title) {
                        color: #17313d;
                        font-weight: 700;
                    }

                    .user-details-grid {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 10px;
                    }

                    .user-details-grid > div {
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                        min-width: 0;
                        padding: 12px 14px;
                        background: #f8fbfc;
                        border: 1px solid #e8eff1;
                        border-radius: 8px;
                    }

                    .user-details-grid span {
                        color: #64748b;
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                    }

                    .user-details-grid strong {
                        overflow-wrap: anywhere;
                        color: #263746;
                        font-size: 13px;
                        font-weight: 600;
                    }

                    .user-management-page .users-table th {
                        color: #405563;
                        background: #f3f8f9;
                        border-bottom: 1px solid #e3ebee;
                        font-size: 11px;
                        text-shadow: none;
                        white-space: nowrap;
                    }

                    .user-management-page .users-table td {
                        color: #475569;
                    }

                    .user-management-page button:focus-visible {
                        outline: 3px solid rgba(var(--app-primary-rgb), 0.28);
                        outline-offset: 2px;
                    }

                    @media (max-width: 768px) {
                        .user-details-grid {
                            grid-template-columns: minmax(0, 1fr);
                        }
                        .user-management-page {
                            padding: 14px;
                        }

                        .search-stats-bar, .filters-section {
                            flex-direction: column;
                            align-items: flex-start;
                        }

                        .search-input {
                            width: 100%;
                            max-width: none;
                        }

                        .user-management-page .search-container {
                            width: 100%;
                            min-width: 0;
                        }

                        .users-table th, .users-table td {
                            padding: 8px 5px;
                            font-size: 13px;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

UserManagement.propTypes = {
    showNotification: PropTypes.func
};

export default connect()(UserManagement);